import { Check, Pencil, Undo2 } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { CountItemView } from '@/api/endpoints/inventory-count-sessions'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  addStockQuantities,
  formatStockDisplay,
} from '@/shared/lib/stock-quantity'

interface CountVarianceRowProps {
  item: CountItemView
  /** The group's reason, or this row's own override — whichever applies to this article. */
  effectiveReason: string | undefined
  /** Opens the article sheet: quantity, reason, and the row's other actions. */
  onOpen: () => void
  /** One-tap confirm with the reason in force; the surplus gate is satisfied in the same call. */
  onConfirm: (recountConfirmed?: boolean) => void
  /** Takes an applied decision back (reverses the correction) — resolved rows only. */
  onReopen: () => void
  highlighted: boolean
  rowRef: (el: HTMLDivElement | null) => void
}

/**
 * One shortage or surplus, as a list row.
 *
 * It used to be a bordered card of roughly 170 px: the article, a reason line with its own 44 px
 * "Grund ändern" button, and a full-width "Bestätigen" below them — two and a half articles per
 * phone screen (CHG-2026-09-inventory-count-review-ux). Everything that repeated per row moved
 * into the article sheet; what stays is the article, its numbers, and the two things a counter
 * does here: open it, or confirm it.
 *
 * The check confirms with the reason in force. Without a reason there is nothing to confirm with,
 * so it opens the sheet instead of sitting there disabled — the counter picks a reason where the
 * pills are, rather than being told no by a grey control.
 *
 * A resolved row keeps its place and offers "Ändern", which reopens it: the correction is reversed
 * by a compensating movement, never edited away. Before this change the row turned into plain text
 * and the article was locked for the rest of the session.
 */
export function CountVarianceRow({
  item,
  effectiveReason,
  onOpen,
  onConfirm,
  onReopen,
  highlighted,
  rowRef,
}: CountVarianceRowProps) {
  const { t } = useTranslation('inventory-count')
  const isSurplus = (item.delta ?? 0) > 0
  const resolved = item.resolution !== 'PENDING'
  // A surplus that was never recounted is confirmed "as counted" in the same call, which is the
  // gate the API enforces; the sheet is where a corrected quantity comes from.
  const needsRecount = isSurplus && !item.recounted
  const detail = t('review.rowDetail', {
    counted: formatStockDisplay(item.countedQty ?? 0),
    expected: formatStockDisplay(
      addStockQuantities(item.expectedAvailable, item.expectedReserved),
    ),
  })

  return (
    <div
      ref={rowRef}
      className={cn(
        'flex min-h-[68px] items-center gap-2.5 border-b border-border px-4 py-3 transition-shadow',
        highlighted && 'ring-2 ring-primary ring-inset',
        resolved && 'bg-muted/40',
      )}
    >
      <button
        type="button"
        onClick={resolved ? onReopen : onOpen}
        className="min-w-0 flex-1 text-left"
      >
        <span
          className={cn(
            'block truncate text-[15px] font-medium leading-5',
            resolved && 'text-muted-foreground',
          )}
        >
          {item.name}
        </span>
        <span className="block truncate text-xs leading-4 tabular-nums text-muted-foreground">
          {resolved
            ? [
                item.reason != null
                  ? t('review.resolvedWithReason', {
                      resolution: t(`review.resolution.${item.resolution}`),
                      reason: t(`review.reason.${item.reason}`),
                    })
                  : t('review.resolvedAs', {
                      resolution: t(`review.resolution.${item.resolution}`),
                    }),
                detail,
              ].join(' · ')
            : detail}
        </span>
        {!resolved && item.affectedOrders.length > 0 && (
          <span className="block truncate text-xs leading-4 tabular-nums text-warning">
            {t('review.affectedOrders', { count: item.affectedOrders.length })}
          </span>
        )}
      </button>

      {resolved ? (
        <Button variant="outline" className="h-11 shrink-0" onClick={onReopen}>
          <Undo2 className="h-4 w-4" />
          {t('review.change')}
        </Button>
      ) : (
        <>
          <span
            className={cn(
              'shrink-0 text-right text-[17px] font-semibold tabular-nums',
              !isSurplus && 'text-destructive',
            )}
          >
            {isSurplus ? '+' : ''}
            {item.delta == null ? null : formatStockDisplay(item.delta)}
          </span>
          <Button
            variant="outline"
            size="icon-lg"
            aria-label={t('review.openItem', { name: item.name })}
            onClick={onOpen}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon-lg"
            aria-label={t('review.confirmItem', { name: item.name })}
            onClick={() =>
              effectiveReason == null
                ? onOpen()
                : onConfirm(needsRecount ? true : undefined)
            }
          >
            <Check className="h-5 w-5" />
          </Button>
        </>
      )}
    </div>
  )
}
